import type { AiCisoConversationMode, ConversationModeRuntime } from './types'

type TranslateFn = (key: string, fallback?: string) => string

/**
 * SEC-004.3C — single Send uses the selected conversation mode (no per-message mode sprawl).
 */
export function DefendSphereAiDispatchButtons({
  runtime,
  draft,
  sending,
  disclosureBusy,
  modeBusy = false,
  selectedMode,
  onDispatch,
  onSend,
  t,
}: {
  runtime: ConversationModeRuntime | null
  draft: string
  sending: boolean
  disclosureBusy: boolean
  modeBusy?: boolean
  selectedMode?: string | null
  onDispatch?: (target?: AiCisoConversationMode) => void
  onSend?: (target?: AiCisoConversationMode) => void
  t: TranslateFn
}) {
  const hasDraft = Boolean(draft.trim())
  const busy = sending || disclosureBusy || modeBusy
  const mode = String(selectedMode || runtime?.conversationMode || 'STANDARDS_RAG').toUpperCase()
  const isExternal = mode === 'EXTERNAL_AI_RAG'
  const externalMeta = runtime?.modes?.find((m) => m.mode === 'EXTERNAL_AI_RAG')
  const externalBlocked = isExternal && externalMeta && !externalMeta.selectable
  const consentPending = isExternal && runtime && runtime.consentSatisfied === false
  const dispatch = onDispatch || onSend || (() => undefined)

  return (
    <div className="ds-ai-dispatch" role="group" aria-label={t('complianceCenterUi.aiCiso.dispatchLabel', 'Ask AI')}>
      <button
        type="button"
        className="ds-ai-dispatch__btn ds-ai-dispatch__btn--standards"
        disabled={busy || !hasDraft || Boolean(externalBlocked) || Boolean(consentPending)}
        data-testid="ai-send"
        title={
          isExternal
            ? t('complianceCenterUi.aiCiso.sendExternalHint', 'Send with external AI (session mode)')
            : t('complianceCenterUi.aiCiso.sendInternalHint', 'Send with internal AI / RAG (session mode)')
        }
        onClick={() => dispatch(isExternal ? 'EXTERNAL_AI_RAG' : undefined)}
      >
        {sending
          ? t('complianceCenterUi.convSending', 'Sending…')
          : t('complianceCenterUi.aiCiso.send', 'Send')}
      </button>
      <div className="ds-ai-dispatch__meta" aria-live="polite">
        <span className="ds-ai-dispatch__hint">
          {isExternal
            ? t('complianceCenterUi.aiCiso.sendUsesExternal', 'Using External AI mode')
            : t('complianceCenterUi.aiCiso.sendUsesInternal', 'Using Internal AI / RAG mode')}
        </span>
      </div>
    </div>
  )
}
